import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const FAQS = [
  {
    category: 'General',
    items: [
      {
        q: 'Do you make a muzzle brake for my rifle?',
        a: 'Most likely yes! We can make one of our clamp-on muzzle brakes for nearly any smooth, sightless barrel up to diameters of just over one inch and for virtually any caliber. Just supply your caliber and barrel measurements as described on the Measuring Information page. We also make brakes for threaded barrels in many imperial and metric thread combinations.',
      },
      {
        q: 'How well do your brakes work?',
        a: 'Our muzzle brakes provide approximately a 60% reduction in recoil and slightly more than that in muzzle rise reduction. This varies depending on caliber — more effective on larger calibers — the length of the barrel, the overall weight of the firearm, and each shooter\'s individual technique.',
      },
      {
        q: 'Will it damage my barrel?',
        a: 'If you apply the Loctite as described in the mounting instructions, that will protect the barrel. While we cannot guarantee this, it should not do any damage. It is also important to get your measurements as accurate as possible — the few reports of scratches on a barrel we have received have been the result of inaccurate barrel measurements.',
      },
      {
        q: 'Will it make my gun louder or quieter?',
        a: 'Since a good portion of the blast is being directed out of the sides and top of the brake, it will increase the apparent volume of the shot to the shooter and those around. We estimate about a 20% perceived increase in volume. If hearing protection is a concern, consider our suppressors or SME products.',
      },
    ],
  },
  {
    category: 'Shipping & Orders',
    items: [
      {
        q: 'Do you ship internationally?',
        a: 'Most likely yes! We ship to most anywhere that the United States Postal Service ships, including Canada, Australia, France, and Sweden. Note: NFA items (suppressors) may only be shipped within the United States to licensed FFL dealers.',
      },
      {
        q: 'What is the lead time for custom orders?',
        a: 'Custom-machined items (clamp-on brakes, custom threaded brakes) are made to order. Lead time is typically 2–3 weeks. You will receive a confirmation email as soon as your order ships. In-stock products typically ship within 5–10 business days.',
      },
    ],
  },
  {
    category: 'Finishes & Materials',
    items: [
      {
        q: 'What finishes are available?',
        a: 'All of our brakes are milled from stainless steel. The uncoated "tumbled" finish is a smooth, semi-matte stainless similar to most stainless barrels. We also offer Cerakote graphite black (matches most black or blued barrels) and FDE (Flat Dark Earth) Cerakote.',
      },
      {
        q: 'How big are your brakes and what do they weigh?',
        a: 'Depending on barrel diameter, our brakes range from 1 to 1.5 inches in diameter. Clamp-on brakes are approximately 3.5 inches long and will increase barrel length by approximately 2 inches. Weight ranges from 6 to 9 ounces depending on diameter.',
      },
    ],
  },
  {
    category: 'Legal & Compliance',
    items: [
      {
        q: 'Are your muzzle brakes legal in my state?',
        a: 'Knowledge of laws governing firearms in your area is your responsibility. We cannot advise you as to what is legal in your locale and cannot be responsible or offer refunds for items not allowed through customs.',
      },
      {
        q: 'What are NFA rules for suppressors?',
        a: 'Suppressors are NFA (National Firearms Act) regulated items. They require an ATF Form 4 transfer through a licensed FFL/SOT dealer and an ATF approval period. All federal NFA rules apply to all suppressor purchases.',
      },
    ],
  },
];

export default function FAQ() {
  const [openItem, setOpenItem] = useState(null);

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-16 lg:pt-20">
      <div className="bg-[#111111] border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <div className="section-divider" />
          <h1 data-testid="faq-heading" className="font-bebas text-5xl lg:text-6xl text-white tracking-wider mb-2">MUZZLE BRAKE FAQ</h1>
          <p className="font-inter text-sm text-zinc-500">Everything you need to know before you buy</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {FAQS.map((section) => (
          <div key={section.category} className="mb-10">
            <h2 className="font-bebas text-2xl text-orange-500 tracking-wider mb-4 uppercase">{section.category}</h2>
            <div className="bg-[#111111] border border-white/10">
              {section.items.map((item, i) => {
                const id = `${section.category}-${i}`;
                const isOpen = openItem === id;
                return (
                  <div key={i} className="border-b border-white/5 last:border-0">
                    <button
                      data-testid={`faq-item-${section.category.toLowerCase()}-${i}`}
                      onClick={() => setOpenItem(isOpen ? null : id)}
                      className="w-full flex items-start justify-between gap-4 px-6 py-5 text-left hover:bg-white/3 transition-colors"
                    >
                      <span className="font-inter text-sm font-medium text-white">{item.q}</span>
                      <div className="flex-shrink-0 mt-0.5">
                        {isOpen ? <ChevronUp size={16} className="text-orange-500" /> : <ChevronDown size={16} className="text-zinc-500" />}
                      </div>
                    </button>
                    {isOpen && (
                      <div className="px-6 pb-5 border-t border-white/5">
                        <p className="font-inter text-sm text-zinc-400 leading-relaxed mt-4">{item.a}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Still have questions */}
        <div className="bg-[#111111] border border-orange-500/20 p-8 text-center mt-8">
          <h2 className="font-bebas text-3xl text-white tracking-wider mb-3">STILL HAVE QUESTIONS?</h2>
          <p className="font-inter text-sm text-zinc-400 mb-5">We're happy to help. Reach out and we'll get you sorted.</p>
          <a href="/contact" data-testid="faq-contact-link" className="inline-block font-bebas text-sm tracking-widest bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 transition-colors">
            CONTACT US →
          </a>
        </div>
      </div>
    </div>
  );
}
